import React from 'react';
import { Helmet } from 'react-helmet-async';
import ClockDisplay from '../components/ClockDisplay';
import RadioAlarm from '../components/RadioAlarm';
import { useFullscreen } from '../contexts/FullscreenContext';
import { getCanonicalUrl } from '../utils/seoUtils';

export default function RadioAlarmPage({ currentTime, radioAlarm }) {
  const { isFullscreen } = useFullscreen();
  
  const {
    alarmHour,
    alarmMinute,
    snoozeMinutes,
    selectedMelody,
    growing,
    alarmActive,
    alarmRinging,
    timeLeft,
    setAlarmHour,
    setAlarmMinute,
    setSnoozeMinutes,
    setSelectedMelody,
    setGrowing,
    setAlarmActive,
    stopAlarm,
    snoozeAlarm,
    testSound,
    // 收音机相关
    selectedStation,
    setSelectedStation,
    volume,
    setVolume,
    isPlaying,
    radioStations,
    playRadio,
    stopRadio,
    testRadio
  } = radioAlarm;

  return (
    <>
      <Helmet>
        <title>Radio Alarm Clock Online - Free Wake-Up Solution | Clock Online</title>
        <meta name="description" content="Wake up to your favorite radio stations from around the world. Free online radio alarm clock with customizable wake-up sounds and snooze functionality." />
        <link rel="canonical" href={getCanonicalUrl('/radio-alarm')} />
        <meta property="og:title" content="Radio Alarm Clock Online - Free Wake-Up Solution | Clock Online" />
        <meta property="og:description" content="Wake up to your favorite radio stations from around the world. Free online radio alarm clock with customizable wake-up sounds and snooze functionality." />
        <meta property="og:url" content={getCanonicalUrl('/radio-alarm')} />
        <meta name="twitter:title" content="Radio Alarm Clock Online - Free Wake-Up Solution | Clock Online" />
        <meta name="twitter:description" content="Wake up to your favorite radio stations from around the world. Free online radio alarm clock." />
      </Helmet>
      {!isFullscreen && (
        <h1 className="text-4xl font-bold text-white text-center mb-8">
          Radio Alarm Clock Online - Free Wake-Up Solution
        </h1>
      )}
      <ClockDisplay currentTime={currentTime} />
      {!isFullscreen && (
        <RadioAlarm
          alarmHour={alarmHour}
          alarmMinute={alarmMinute}
          snoozeMinutes={snoozeMinutes}
          selectedMelody={selectedMelody}
          growing={growing}
          alarmActive={alarmActive}
          alarmRinging={alarmRinging}
          timeLeft={timeLeft}
          setAlarmHour={setAlarmHour}
          setAlarmMinute={setAlarmMinute}
          setSnoozeMinutes={setSnoozeMinutes}
          setSelectedMelody={setSelectedMelody}
          setGrowing={setGrowing}
          setAlarmActive={setAlarmActive}
          stopAlarm={stopAlarm}
          snoozeAlarm={snoozeAlarm}
          testSound={testSound}
          selectedStation={selectedStation}
          setSelectedStation={setSelectedStation}
          volume={volume}
          setVolume={setVolume}
          isPlaying={isPlaying}
          radioStations={radioStations}
          playRadio={playRadio}
          stopRadio={stopRadio}
          testRadio={testRadio}
        />
      )}
      
      {!isFullscreen && (
        <div className="mt-16 max-w-4xl mx-auto px-6 text-white">
          <h2 className="text-3xl font-bold text-center mb-8 text-blue-300">
            Complete Guide to Radio Alarm Clock Online
          </h2>
          
          <div className="space-y-8 text-lg leading-relaxed">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Getting Started with Radio Alarm Clock Online</h3>
              <p className="mb-4">
                Welcome to the ultimate Radio Alarm Clock Online experience! Our innovative platform transforms your device into a powerful wake-up companion that combines the reliability of traditional alarm clocks with the convenience of modern technology. Whether you're a heavy sleeper, a light sleeper, or someone who struggles with morning routines, our Radio Alarm Clock Online is designed to make waking up a pleasant and energizing experience.
              </p>
              <p className="mb-4">
                Setting up your Radio Alarm Clock Online is incredibly simple. Just select your preferred wake-up time using our intuitive time picker, choose from our extensive collection of high-quality alarm sounds, and activate your alarm with a single click. The Radio Alarm Clock Online will then work silently in the background, ensuring you wake up exactly when you need to, every single time.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">♪ Premium Sound Experience</h3>
              <p className="mb-4">
                What sets our Radio Alarm Clock Online apart is our carefully curated selection of wake-up sounds. From gentle nature sounds that ease you into consciousness to energetic melodies that get you moving, every sound in our Radio Alarm Clock Online library has been professionally optimized for the perfect wake-up experience. You can preview each sound before setting your alarm, ensuring your Radio Alarm Clock Online uses exactly the tone that works best for your sleep patterns.
              </p>
              <p className="mb-4">
                Our Radio Alarm Clock Online features include classic alarm tones, soothing chimes, nature sounds like birds chirping and ocean waves, and even upbeat digital melodies. The growing volume feature gradually increases the sound level, allowing your Radio Alarm Clock Online to wake you naturally without the jarring shock of sudden loud noises.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">● Smart Alarm Features</h3>
              <p className="mb-4">
                The Radio Alarm Clock Online includes intelligent snooze functionality that adapts to your sleep habits. Set your preferred snooze duration anywhere from 1 to 60 minutes, and your Radio Alarm Clock Online will remember this setting for future use. The visual countdown timer shows exactly how much time remains until your next wake-up call, helping you make informed decisions about those precious extra minutes of sleep.
              </p>
              <p className="mb-4">
                Our Radio Alarm Clock Online also features a test sound function, allowing you to preview your selected alarm tone at any time. This ensures your Radio Alarm Clock Online is set to the perfect volume and sound before you go to sleep, eliminating any morning surprises and guaranteeing a smooth wake-up experience.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">★ Advanced Functionality</h3>
              <p className="mb-4">
                Beyond basic alarm functionality, our Radio Alarm Clock Online offers advanced features that make it the most comprehensive wake-up solution available. The growing alarm feature starts quietly and gradually increases in volume, mimicking natural sunrise patterns to wake you more gently. This scientifically-backed approach helps reduce morning grogginess and makes your Radio Alarm Clock Online experience more pleasant.
              </p>
              <p className="mb-4">
                The Radio Alarm Clock Online works seamlessly across all devices and browsers, requiring no downloads or installations. Whether you're using a smartphone, tablet, laptop, or desktop computer, your Radio Alarm Clock Online maintains consistent performance and reliability. The responsive design ensures your Radio Alarm Clock Online looks and functions perfectly on any screen size.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">◆ Pro Tips for Better Sleep</h3>
              <p className="mb-4">
                To maximize the effectiveness of your Radio Alarm Clock Online, establish a consistent sleep schedule by going to bed and waking up at the same time every day. Place your device at an appropriate distance where the Radio Alarm Clock Online can be clearly heard but won't be too jarring. Consider using the nature sounds option in your Radio Alarm Clock Online for a more peaceful wake-up experience.
              </p>
              <p className="mb-4">
                For heavy sleepers, we recommend using the growing alarm feature with a more energetic melody in your Radio Alarm Clock Online settings. Light sleepers might prefer the gentle chimes or nature sounds. Experiment with different combinations to find what works best for your unique sleep patterns and preferences with the Radio Alarm Clock Online.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▲ Start Using Radio Alarm Clock Online Today</h3>
              <p className="mb-4">
                Ready to transform your mornings? Your Radio Alarm Clock Online is just a few clicks away from revolutionizing your wake-up routine. Set your first alarm now and experience the difference that a professionally designed Radio Alarm Clock Online can make in your daily life. Join thousands of satisfied users who have already discovered the power of our Radio Alarm Clock Online platform.
              </p>
              <p className="mb-4">
                Remember, consistency is key to developing healthy sleep habits. Use your Radio Alarm Clock Online every day, even on weekends, to maintain your circadian rhythm and enjoy better sleep quality. Your future self will thank you for choosing the most reliable and user-friendly Radio Alarm Clock Online available today.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}